/* eslint-disable react/sort-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEqual, sortBy } from 'lodash';

const WithTranslations = (WrappedComponent) =>
  class WithTranslationsComponent extends Component {
    static manifest = Object.freeze({
      ...WrappedComponent.manifest,
      // languageTranslators — kept for locale/translator info
      languageTranslators: {
        type: 'okapi',
        path: 'languageTranslators?limit=1000',
        records: 'languageTranslators',
        accumulate: true,
        fetch: false,
        resourceShouldRefresh: true,
      },
    });

    static propTypes = {
      stripes: PropTypes.shape({
        connect: PropTypes.func.isRequired,
        okapi: PropTypes.shape({
          url: PropTypes.string.isRequired,
          tenant: PropTypes.string.isRequired,
          token: PropTypes.string,
        }),
      }).isRequired,
      resources: PropTypes.shape({
        languageTranslators: PropTypes.shape({
          records: PropTypes.arrayOf(PropTypes.object),
        }),
      }),
      mutator: PropTypes.shape({
        languageTranslators: PropTypes.shape({
          GET: PropTypes.func.isRequired,
          reset: PropTypes.func.isRequired,
        }),
      }),
    };

    state = {
      languageTranslators: [],
    };

    static getDerivedStateFromProps(nextProps, state) {
      const languageTranslators = sortBy(
        (nextProps.resources.languageTranslators || {}).records || [],
        ['localeValue'],
      );
      if (!isEqual(languageTranslators, state.languageTranslators)) {
        return { languageTranslators };
      }
      return null;
    }

    componentDidMount() {
      this.props.mutator.languageTranslators.reset();
      this.props.mutator.languageTranslators.GET();
    }

    // ── Locale / translator helpers (unchanged) ─────────────────────

    getLocaleTranslators = () => this.state.languageTranslators;

    getAssignedLocales = () =>
      this.getLocaleTranslators().map((loc) => loc.localeValue);

    ifAssignedLocale = (localeValue) =>
      this.getLocaleTranslators().some(
        (loc) => loc.localeValue === localeValue,
      );

    getLocaleTranslatorsByValue = (localeValue) => {
      const translators = [];
      this.getLocaleTranslators()
        .filter((loc) => loc.localeValue === localeValue)
        .forEach((trans) => {
          trans.translators.forEach((el) => translators.push(el.value));
        });
      return translators;
    };

    // ── Translation key helpers (new API) ───────────────────────────

    getOkapiHeaders = () => {
      const { okapi } = this.props.stripes;
      return {
        'X-Okapi-Tenant': okapi.tenant,
        'X-Okapi-Token': okapi.token,
        'Content-Type': 'application/json',
      };
    };

    /**
     * Upsert translation keys via POST /translation-keys/import.
     * Used when:
     *   - A new CV item is saved (isSourceImport=true, conflictStrategy='skip')
     *   - A translated value is updated (isSourceImport=false, conflictStrategy='overwrite')
     *   - Bulk key generation from ActionsMenu
     */
    updateTranslations = (
      localeValue,
      categoryName,
      entries,
      { isSourceImport = false, conflictStrategy = 'overwrite' } = {},
    ) => {
      if (!entries || Object.keys(entries).length === 0) return;

      const { okapi } = this.props.stripes;
      fetch(`${okapi.url}/translation-keys/import`, {
        method: 'POST',
        headers: this.getOkapiHeaders(),
        body: JSON.stringify({
          localeValue,
          categoryName,
          isSourceImport,
          conflictStrategy,
          entries,
        }),
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.error('WithTranslations.updateTranslations failed', e);
      });
    };

    /**
     * Delete all locale translations for a single translationKey.
     * Used when a CV item is deleted.
     * Calls DELETE /translation-keys/by-key (atomic, all locales).
     */
    deleteTranslationKey = (translationKey, categoryName) => {
      const { okapi } = this.props.stripes;
      return fetch(`${okapi.url}/translation-keys/by-key`, {
        method: 'DELETE',
        headers: this.getOkapiHeaders(),
        body: JSON.stringify({ translationKey, categoryName }),
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.error('WithTranslations.deleteTranslationKey failed', e);
      });
    };

    /**
     * Bulk delete translation keys by flexible filter.
     * Calls DELETE /translation-keys/bulk.
     * filter: { translationKeys[], tableName, appName, categoryName, localeValue }
     */
    bulkDeleteTranslationKeys = (filter) => {
      const { okapi } = this.props.stripes;
      return fetch(`${okapi.url}/translation-keys/bulk`, {
        method: 'DELETE',
        headers: this.getOkapiHeaders(),
        body: JSON.stringify({ filter }),
      }).catch((e) => {
        // eslint-disable-next-line no-console
        console.error('WithTranslations.bulkDeleteTranslationKeys failed', e);
      });
    };

    render() {
      return (
        <WrappedComponent
          getLocaleTranslators={this.getLocaleTranslators}
          getAssignedLocales={this.getAssignedLocales}
          getLocaleTranslatorsByValue={this.getLocaleTranslatorsByValue}
          ifAssignedLocale={this.ifAssignedLocale}
          updateTranslations={this.updateTranslations}
          deleteTranslationKey={this.deleteTranslationKey}
          bulkDeleteTranslationKeys={this.bulkDeleteTranslationKeys}
          {...this.props}
        />
      );
    }
  };

export default WithTranslations;
